/**
 * What the screen looks like, as opposed to what the session is: which preset, which mode override,
 * the open panels, the draft of the current screen. One store, written only through these functions.
 */
import type { Preset } from '../layout/model';
import type { DeviceSettings } from './appearance';
import type { OpenDetail } from './detail';
import { createStore, type Store } from './store';
import type { AccessibilityMode } from './wire';
import type { OpenMenu } from '../packs/menu';

export interface UiState {
  presetId: string;
  /** A preset being edited live (the layout editor); wins over `presetId` until cleared. */
  draftPreset: Preset | null;
  /** A mode chosen on this screen (dev, URL, or a domain without the backend button): wins over the session's. */
  modeOverride: AccessibilityMode | null;
  /** Which side panel is open next to the canvas. */
  panel: 'none' | 'dev' | 'replay' | 'operator' | 'review';
  /** The screen the draft belongs to; a new screen starts an empty draft. */
  draftScreen: string | null;
  draft: Record<string, unknown>;
  /**
   * An overlay a reply asked for, until closed or the screen changes. With `closeAfterMs` it closes by
   * itself that long after the reply or the last touch on it (`closeAt`: when, as it stands).
   */
  overlay: { widget: string; props: unknown; turnId: string; closeAfterMs: number | null; closeAt: number | null } | null;
  /** The detail of a shown item a reply opened on its card (core/detail.ts), until closed. */
  detail: OpenDetail | null;
  /** "Start over" asks for the language again, as on the old kiosk (the backend keeps the one chosen). */
  askLanguage: boolean;
  /** A main-bar menu open in place of the actions (packs/menu.ts); closed when the screen changes. */
  menu: OpenMenu | null;
  modePickerOpen: boolean;
  /** What Settings changed on this device (the theme, and the look over the theme's and preset's). */
  settings: DeviceSettings;
  settingsOpen: boolean;
  /** A cloud passing over the canvas while the look changes under it (ui-actions `veiled`): when it began, till it passed. */
  veil: number | null;
}

export function createUiStore(initial: Partial<UiState> = {}): Store<UiState> {
  return createStore<UiState>('ui', {
    presetId: 'kiosk',
    draftPreset: null,
    modeOverride: null,
    panel: 'none',
    draftScreen: null,
    draft: {},
    overlay: null,
    detail: null,
    askLanguage: false,
    menu: null,
    modePickerOpen: false,
    settings: {},
    settingsOpen: false,
    veil: null,
    ...initial,
  });
}

/** Keep the draft, the overlay and an open detail tied to the screen they were made on. */
export function onScreen(ui: Store<UiState>, screen: string | null): void {
  ui.set((s) => (s.draftScreen === screen ? s : { ...s, draftScreen: screen, draft: {}, overlay: null, detail: null, menu: null }));
}

export function patchDraft(ui: Store<UiState>, patch: Record<string, unknown>): void {
  ui.set((s) => ({ ...s, draft: { ...s.draft, ...patch } }));
}
